import { Option, Select, TextField, Text } from "@simplybusiness/mobius";
import type { QuestionComponentProps } from "../../types";
import { useLocale } from "../../../../../../../hooks/useLocale";
import { useQuestionnaireActions } from "../../../../../../../features/questionnaire";
import { useEffect, useRef } from "react";
import type React from "react";
import { QuestionLabel } from "../../../QuestionLabel";
import {
  shouldShowCustomInput,
  getValidationErrorMessage,
  getCustomDefaultValue,
  getDisplayOptions,
} from "./utils";
import { getErrorMessage } from "../../../../../validation/question/getErrorMessage";
import "./AnnualTurnoverQuestion.css";

export type AnnualTurnoverConfig = {
  customMaximumValue?: number;
  optionalPrimaryText?: string;
};

export const AnnualTurnoverQuestion = ({
  questionFieldProps,
  options,
  config,
}: QuestionComponentProps) => {
  const {
    value,
    onChange,
    onBlur,
    placeholder,
    questionLabelProps,
    helpInfo,
    errorMessage,
    isInvalid,
    ...otherProps
  } = questionFieldProps;

  const { isUK } = useLocale();
  const currencySymbol = isUK ? "£" : "$";
  const { setError, removeError } = useQuestionnaireActions();
  const customInputRef = useRef<HTMLInputElement>(null);

  // Get the last option (the "Over £X" option)
  const lastOption = options?.[options.length - 1];
  const lastOptionValue = lastOption?.value ?? "";

  // Determine if we should show the custom input
  const currentValue = (value as string) ?? "";
  const showCustomInput = shouldShowCustomInput(currentValue, lastOptionValue);

  // Get config values
  const { customMaximumValue, optionalPrimaryText } =
    (config as AnnualTurnoverConfig) ?? {};

  // Get the default value for the custom input
  const customDefaultValue = getCustomDefaultValue(
    currentValue,
    lastOptionValue,
  );

  // Clear error when component unmounts
  useEffect(() => {
    return () => {
      removeError(questionFieldProps.name);
    };
  }, [questionFieldProps.name, removeError]);

  if (!options?.length) {
    console.warn("AnnualTurnoverQuestion: No required options found");
    return null;
  }

  // Handle select change
  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value;
    const isNotLastOption = selectedValue !== lastOptionValue;

    // If user selects a non-last option, clear the error
    if (isNotLastOption) {
      removeError(questionFieldProps.name);
    }

    // Update the answer with the selected value
    onChange?.({
      target: {
        name: questionFieldProps.name,
        value: selectedValue,
      },
    });
  };

  // Handle custom input blur
  const handleCustomInputBlur = (event: React.FocusEvent<Element>) => {
    const inputValue = (event.target as HTMLInputElement).value;
    const isValidInput = customInputRef.current?.checkValidity();

    if (!isValidInput) {
      const numValue = Number(inputValue);
      const minValue = Number(lastOptionValue);
      const clientSetErrorMessage = getValidationErrorMessage(
        numValue,
        minValue,
        customMaximumValue,
        currencySymbol,
      );

      const manualErrorMessage = getErrorMessage({
        questionErrorMessage: errorMessage,
        fallbackMessage: clientSetErrorMessage,
      });
      setError(questionFieldProps.name, manualErrorMessage!);
      return;
    }

    // Clear error if valid
    removeError(questionFieldProps.name);

    // Only update answer if there's a value
    if (inputValue) {
      onBlur?.({
        ...event,
        target: {
          name: questionFieldProps.name,
          value: inputValue,
        },
      });
    }
  };

  // Generate the primary text for the custom input
  const customInputPrimaryText =
    optionalPrimaryText ||
    `Please enter your annual turnover if it's ${lastOption?.label.toLowerCase() ?? ""}`;

  // Add placeholder option if provided
  const displayOptions = getDisplayOptions(placeholder, options);

  return (
    <>
      <Select
        {...otherProps}
        value={showCustomInput ? lastOptionValue : currentValue}
        onChange={handleSelectChange}
        errorMessage={showCustomInput ? undefined : errorMessage}
        isInvalid={showCustomInput ? false : isInvalid}
        className={
          showCustomInput
            ? "mobius-question__annual-turnover-select"
            : undefined
        }
      >
        {displayOptions.map(option => (
          <Option key={option.value} value={option.value}>
            {option.label}
          </Option>
        ))}
      </Select>

      {showCustomInput && (
        <>
          <QuestionLabel
            {...questionLabelProps}
            primaryText={customInputPrimaryText}
            secondaryText={undefined}
          />
          <TextField
            {...otherProps}
            errorMessage={errorMessage}
            isInvalid={isInvalid}
            ref={customInputRef}
            type="number"
            defaultValue={customDefaultValue}
            onBlur={handleCustomInputBlur}
            isRequired={true}
            prefixInside={<Text>{currencySymbol}</Text>}
            min={Number(lastOptionValue)}
            max={customMaximumValue}
          />
        </>
      )}
    </>
  );
};
